import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';
import { CloudService, ServiceCardData } from '../../core/services/cloud.service';
import { AzureConnection } from '../../core/services/azure.service';
import { AZURE_SERVICES } from '../../core/cloud-catalog';

export type AzureOverviewState = 'loading' | 'not-connected' | 'connected' | 'error';

/**
 * Azure Cloud overview body — the Azure counterpart to the AWS card grid.
 *
 * Presentational: the parent shell owns the Azure connection + the active
 * subscription/location scope (bound to ?account=/?region=, exactly like AWS)
 * and passes them down. This component renders the Core-6 service cards, loads
 * each card's live resource count independently for the active scope, and
 * derives an intentional per-card status (Healthy when resources exist, Idle
 * when a connected service simply has none — never a blanket Unknown). Cards
 * navigate to their real detail pages.
 */
@Component({
  selector: 'app-azure-overview',
  templateUrl: './azure-overview.component.html',
})
export class AzureOverviewComponent implements OnChanges, OnDestroy {
  /** Connection + scope, owned by the shell (single source of truth). */
  @Input() connection: AzureConnection | null = null;
  @Input() state: AzureOverviewState = 'loading';
  @Input() subscription = '';
  @Input() location = 'all';
  @Input() monitoringLoaded = false;
  @Input() monitoredCount = 0;

  cards: ServiceCardData[] = AZURE_SERVICES.map(s => ({
    id: s.id, name: s.name, icon: s.icon, route: s.route, status: 'idle',
    metrics: [{ label: s.metricLabel, value: '—' }],
    ...(s.scope ? { scope: s.scope } : {}),
  }));

  private readonly loadingCards = new Set<string>();
  private countsSub?: Subscription;
  /** Bumped on every scope load so a late response from a prior scope is dropped. */
  private loadGeneration = 0;

  constructor(readonly cloudService: CloudService) {}

  ngOnChanges(changes: SimpleChanges): void {
    // (Re)load counts whenever the active scope changes or we (re)enter the
    // connected state. The shell sets the cloud service's account/scope before
    // these inputs propagate, so getAzureResources() reads the correct scope.
    const scopeChanged = changes['subscription'] || changes['location'] || changes['state'];
    if (scopeChanged && this.state === 'connected' && this.subscription) {
      this.loadCounts();
    }
  }

  /**
   * Per-card resource counts from ONE batched backend query (not one request per
   * card) — a single Azure Resource Graph call for the whole grid, which avoids
   * the 429 throttling that ~20 parallel queries triggered.
   */
  private loadCounts(): void {
    const generation = ++this.loadGeneration;
    this.countsSub?.unsubscribe();
    this.loadingCards.clear();
    AZURE_SERVICES.forEach(s => this.loadingCards.add(s.id));
    // Reset to placeholders so no stale previous-scope numbers linger.
    this.cards = this.cards.map(c => ({ ...c, status: 'idle', metrics: [{ ...c.metrics[0], value: '—' }] }));

    this.countsSub = this.cloudService.getAzureResourceCounts().subscribe(counts => {
      if (generation !== this.loadGeneration) return; // stale scope — ignore
      this.cards = this.cards.map(c => {
        const svc = AZURE_SERVICES.find(s => s.id === c.id);
        const count = svc ? (counts[svc.serviceType] ?? 0) : 0;
        return { ...c, status: count > 0 ? 'healthy' : 'idle', metrics: [{ ...c.metrics[0], value: String(count) }] };
      });
      this.loadingCards.clear();
    });
  }

  isCardLoading(id: string): boolean { return this.loadingCards.has(id); }

  get subscriptionLabel(): string {
    const subs = this.connection?.subscriptions ?? [];
    const active = subs.find(s => s.subscriptionId === this.subscription)
      ?? subs.find(s => s.isPrimary) ?? subs[0];
    return active ? (active.label || active.subscriptionId) : '—';
  }

  ngOnDestroy(): void {
    this.countsSub?.unsubscribe();
  }
}
